import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  CalendarDays, Calendar, User, Tag, Clock, ArrowLeft,
  Bookmark, Share2, ThumbsUp, MessageSquare,
  ExternalLink, Newspaper, FileText, Heart, Eye
} from 'lucide-react';

const BlogView = () => {
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formattedContent, setFormattedContent] = useState('');
  const { id } = useParams(); // Use slug from URL params instead of id
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [likes, setLikes] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);

  useEffect(() => {
    const fetchRelatedPosts = async () => {
      try {
        const response = await fetch('https://apigulmohar.dodunsoftsolutions.com/api/posts');
        const data = await response.json();

        // Filter posts with common tags or same type
        const filtered = data.filter((p) =>
          p.slug !== post.slug && (
            (p.tags?.some(tag => post.tags?.includes(tag)) ||
              p.type === post.type)
          )
        ).slice(0, 3);

        setRelatedPosts(filtered);
      } catch (err) {
        console.error('Error fetching related posts:', err);
      }
    };

    if (post && (post.tags?.length > 0 || post.type)) {
      fetchRelatedPosts();
    }
  }, [post, id]);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setIsLoading(true);
        // Using slug instead of id
        const response = await fetch(`https://apigulmohar.dodunsoftsolutions.com/api/posts/${id}`);

        if (!response.ok) {
          throw new Error('Failed to fetch post');
        }

        const result = await response.json();
        setPost(result.data); // ✅ Only set the 'data' part here
        console.log(result.data);
        setLikes(Math.floor(Math.random() * 50)); // Mock likes count
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching post:', err);
      } finally {
        // Add a slight delay to show the loading animation
        setTimeout(() => setIsLoading(false), 600);
      }
    };

    fetchPost();
  }, [id]);

  useEffect(() => {
    if (post && post.content) {
      // Process the content to add line breaks after periods
      const processed = formatContentWithLineBreaks(post.content);
      setFormattedContent(processed);
    }
  }, [id, post]);

  // Function to format content with line breaks
  const formatContentWithLineBreaks = (content) => {
    // Split the content by periods followed by a space
    const sentences = content.split(/\.\s/);

    let result = '';
    let lineCount = 0;

    // Process each sentence
    sentences.forEach((sentence, index) => {
      if (index < sentences.length - 1) {
        // Add the sentence back with its period
        result += sentence + '. ';

        lineCount++;

        // After every 4-5 lines, add a line break
        if (lineCount >= 4 && lineCount <= 5) {
          result += '</br> ';
          lineCount = 0;
        }
      } else {
        // Handle the last sentence (which might not end with a period)
        result += sentence;
      }
    });

    return result;
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const getReadTime = (content) => {
    // Assuming an average reading speed of 200 words per minute
    const wordCount = content ? content.split(/\s+/).length : 0;
    const readTime = Math.ceil(wordCount / 200);
    return readTime > 0 ? readTime : 1; // Minimum 1 minute read time
  };

  // Calculate reading time based on content length
  const calculateReadingTime = (content) => {
    if (!content) return 1;
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    const readingTime = Math.ceil(wordCount / wordsPerMinute);
    return readingTime < 1 ? 1 : readingTime;
  };

  const handleLike = () => {
    setHasLiked(!hasLiked);
    setLikes(prev => hasLiked ? prev - 1 : prev + 1);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post?.title,
        text: post?.excerpt,
        url: window.location.href,
      })
        .catch(err => console.error('Error sharing:', err));
    } else {
      // Fallback - copy to clipboard
      navigator.clipboard.writeText(window.location.href)
        .then(() => alert('Link copied to clipboard!'))
        .catch(err => console.error('Could not copy text: ', err));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="animate-pulse flex flex-col items-center space-y-8 w-full max-w-4xl">
          <div className="h-6 w-32 bg-gray-300 dark:bg-gray-700 rounded-md"></div>
          <div className="h-16 w-3/4 bg-gray-300 dark:bg-gray-700 rounded-md"></div>
          <div className="h-72 w-full bg-gray-300 dark:bg-gray-700 rounded-lg"></div>
          <div className="w-full space-y-4">
            <div className="h-4 w-full bg-gray-300 dark:bg-gray-700 rounded"></div>
            <div className="h-4 w-5/6 bg-gray-300 dark:bg-gray-700 rounded"></div>
            <div className="h-4 w-4/6 bg-gray-300 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="text-red-500 text-6xl mb-6">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Unable to load content</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8">{error}</p>
          <button
            onClick={() => window.history.back()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md transition-colors flex items-center mx-auto transform hover:scale-105 duration-200"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="text-gray-400 dark:text-gray-500 text-6xl mb-6">🔍</div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Content not found</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8">The post you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => window.history.back()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md transition-colors flex items-center mx-auto transform hover:scale-105 duration-200"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const readingTime = post.content ? calculateReadingTime(post.content) : null;
  const isNews = post.type === 'news';

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Hero section with parallax effect */}
      <div className="relative overflow-hidden">
        <div
          className="w-full h-[720px] md:h-screen md:max-h-[550px] bg-cover bg-center bg-fixed"
          style={{ backgroundImage: `url(${post.coverImage || 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&q=80'})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/80"></div>
        </div>

        <div className="container mx-auto px-4 absolute inset-0 flex flex-col justify-end pb-12">
          <div className="relative z-10 max-w-4xl mx-auto">
            {/* Status badges */}
            <div className="flex flex-wrap gap-2 mb-4">
              {post.status === 'draft' && (
                <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-medium inline-flex items-center">
                  Draft
                </span>
              )}
              {post.status === 'archived' && (
                <span className="bg-gray-500 text-white px-3 py-1 rounded-full text-sm font-medium inline-flex items-center">
                  Archived
                </span>
              )}
              {post.isFeatured && post.status === 'published' && (
                <span className="bg-gradient-to-r from-purple-600 to-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium inline-flex items-center">
                  <Heart className="w-3 h-3 mr-1" fill="white" />
                  Featured
                </span>
              )}
            </div>

            {/* Title with animation */}
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight animate-fadeInSlide">
              {post.title}
            </h1>

            {/* Author and metadata */}
            <div className="flex flex-wrap items-center text-white gap-4 text-sm md:text-base animate-fadeInUp">
              <div className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center">
                <CalendarDays className="w-4 h-4 mr-2" />
                <span>{formatDate(post.publishedAt)}</span>
              </div>
              {readingTime && (
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-2" />
                  <span>{readingTime} min read</span>
                </div>
              )}
              <div className="flex items-center">
                <Eye className="w-4 h-4 mr-2" />
                <span>{Math.floor(Math.random() * 1000) + 100} views</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-auto mx-auto">

          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden transform transition-all duration-500 animate-fadeIn">
            {/* Blog content */}
            <div className="p-6 md:p-12">
              {/* News URL if present */}
              {isNews && post.newsUrl && (
                <div className="mb-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 flex items-center justify-between">
                  <div className="text-gray-700 dark:text-gray-300 font-medium">Original news source:</div>
                  <a
                    href={post.newsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium transition-colors"
                  >
                    Visit Source
                    <ExternalLink className="w-4 h-4 ml-1" />
                  </a>
                </div>
              )}

              {/* Excerpt with styled quote */}
              {post.excerpt && (
                <div className="text-xl text-gray-600 dark:text-gray-300 mb-10 font-serif italic border-l-4 border-blue-500 pl-6 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-r-lg">
                  {post.excerpt}
                </div>
              )}

              {/* Main content with improved typography */}
              {post.content && (
                <div className="text-justify dark:text-white prose prose-lg md:prose-xl max-w-none prose-headings:text-blue-900 dark:prose-headings:text-blue-300 prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-a:no-underline prose-a:font-medium hover:prose-a:text-blue-800 dark:hover:prose-a:text-blue-300 hover:prose-a:underline prose-img:rounded-xl prose-img:shadow-md dark:prose-invert">
                  {/* Render content as HTML */}
                  <div dangerouslySetInnerHTML={{ __html: formattedContent }} />
                </div>
              )}

              {/* Tags with improved styling */}
              {post.tags && post.tags.length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-4 flex items-center">
                    <Tag className="w-4 h-4 mr-2" />
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="bg-gray-100 dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/40 text-gray-800 dark:text-gray-200 hover:text-blue-700 dark:hover:text-blue-300 px-4 py-2 rounded-full text-sm cursor-pointer transition-colors border border-transparent hover:border-blue-200 dark:hover:border-blue-700"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Author profile */}
              <div className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-start gap-5">
                  <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden flex-shrink-0 flex items-center justify-center">
                    <User className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100">{post.author}</h3>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">Content Creator</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Related posts section with improved cards */}
      {relatedPosts.length > 0 && (
        <div className="container mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold mb-8 text-gray-800 dark:text-gray-100 flex items-center">
            <span className="bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 p-2 rounded-lg mr-3">
              <FileText className="w-5 h-5" />
            </span>
            Related {post.type === 'news' ? 'News' : 'Blog Posts'}
          </h2>
          <div className="grid gap-8 md:grid-cols-3">
            {relatedPosts.map((related, index) => (
              <div key={index} className="group bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition duration-300 h-full flex flex-col border transform hover:-translate-y-1">
                <div className="relative aspect-video overflow-hidden bg-gray-100 dark:bg-gray-700">
                  <img
                    src={related.coverImage || "/api/placeholder/600/400"}
                    alt={related.title}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition duration-500"
                  />
                  <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                  {related.status !== 'published' && (
                    <div className="absolute top-3 left-3 bg-gray-900/80 backdrop-blur-sm text-white text-xs font-medium px-2.5 py-1 rounded-full">
                      {related.status}
                    </div>
                  )}

                  {/* Type badge */}
                  <div className="absolute top-3 right-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm text-xs font-medium px-2.5 py-1 rounded-full flex items-center">
                    {related.type === 'news' ? (
                      <>
                        <Newspaper className="w-3 h-3 mr-1 text-red-500" />
                        <span className="text-red-500 dark:text-red-400">News</span>
                      </>
                    ) : (
                      <>
                        <FileText className="w-3 h-3 mr-1 text-blue-500" />
                        <span className="text-blue-500 dark:text-blue-400">Blog</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex-1">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {related.tags && related.tags.map((tag, idx) => (
                        idx < 2 ? (
                          <span
                            key={idx}
                            className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs font-medium px-2.5 py-0.5 rounded-full"
                          >
                            {tag}
                          </span>
                        ) : null
                      ))}
                      {related.tags && related.tags.length > 2 && (
                        <span className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 text-xs font-medium px-2.5 py-0.5 rounded-full">
                          +{related.tags.length - 2}
                        </span>
                      )}
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition duration-200 line-clamp-2">
                      {related.title}
                    </h3>
                    {related.excerpt && (
                      <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-3">
                        {related.excerpt}
                      </p>
                    )}

                    {/* External link for news */}
                    {related.type === 'news' && related.newsUrl && (
                      <a
                        href={related.newsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium flex items-center mt-2 mb-4"
                      >
                        Read original source
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    )}
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
                    <div className="flex items-center space-x-1">
                      <User className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">{related.author}</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      {related.content && (
                        <div className="flex items-center space-x-1">
                          <Clock className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                          <span className="text-sm text-gray-600 dark:text-gray-400">{getReadTime(related.content)} min</span>
                        </div>
                      )}
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">{formatDate(related.publishedAt).split(",")[0]}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add custom CSS for animations */}
      <style jsx>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.5s ease-out forwards;
                }
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeInUp {
                    animation: fadeInUp 0.6s ease-out forwards;
                }
                @keyframes fadeInSlide {
                    from { opacity: 0; transform: translateY(15px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeInSlide {
                    animation: fadeInSlide 0.7s ease-out forwards;
                }
            `}</style>
    </div>
  );
};

export default BlogView;